// Figma: 6387:57385 — see stories/blocks/marquee-track.stories.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type MarqueeDirection = "ltr" | "rtl";

/** Bake `reverse` into Tailwind arbitrary animation classes for LTR rows. */
export function resolveMarqueeAnimationClass(
  animationClassName: string,
  direction: MarqueeDirection
): string {
  if (direction !== "ltr" || animationClassName.includes("reverse")) {
    return animationClassName;
  }

  const closingBracketIndex = animationClassName.lastIndexOf("]");
  if (closingBracketIndex === -1) return animationClassName;

  return `${animationClassName.slice(0, closingBracketIndex)}_reverse${animationClassName.slice(closingBracketIndex)}`;
}

interface MarqueeTrackProps {
  children: ReactNode;
  /** `rtl` scrolls content right-to-left; `ltr` scrolls left-to-right. */
  direction?: MarqueeDirection;
  /** Literal Tailwind animation utility — must be static for JIT. */
  animationClassName?: string;
  paused?: boolean;
  pauseOnHover?: boolean;
  className?: string;
  trackClassName?: string;
}

/**
 * Seamless horizontal marquee track. Duplicates its children in the parent;
 * animates via the shared `values-marquee` keyframe (-50% translateX).
 * Waits for descendant images before starting so track width stays stable.
 */
export function MarqueeTrack({
  children,
  direction = "rtl",
  animationClassName = "[animation:values-marquee_60s_linear_infinite]",
  paused = false,
  pauseOnHover = true,
  className,
  trackClassName,
}: MarqueeTrackProps) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const root = trackRef.current;
    if (!root) return;

    // Only gate on the first loop's images — duplicated aria-hidden tiles may
    // never lazy-load, which previously blocked the marquee from starting.
    const imgs = Array.from(
      root.querySelectorAll<HTMLImageElement>(
        "li:not([aria-hidden='true']) img"
      )
    );

    let cancelled = false;

    const markReady = () => {
      if (!cancelled) setReady(true);
    };

    const timeout = window.setTimeout(markReady, 2500);

    if (imgs.length === 0) {
      markReady();
      return () => {
        cancelled = true;
        window.clearTimeout(timeout);
      };
    }

    let settled = 0;
    const onSettled = () => {
      settled += 1;
      if (settled >= imgs.length) markReady();
    };

    for (const img of imgs) {
      if (img.complete) onSettled();
      else {
        img.addEventListener("load", onSettled, { once: true });
        img.addEventListener("error", onSettled, { once: true });
      }
    }

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      for (const img of imgs) {
        img.removeEventListener("load", onSettled);
        img.removeEventListener("error", onSettled);
      }
    };
  }, [children]);

  return (
    <div className={cn("group/marquee flex overflow-hidden", className)}>
      <ul
        ref={trackRef}
        className={cn(
          "flex w-max",
          ready && resolveMarqueeAnimationClass(animationClassName, direction),
          pauseOnHover &&
            "group-hover/marquee:[animation-play-state:paused] motion-reduce:[animation-play-state:paused]",
          paused && "[animation-play-state:paused]",
          trackClassName
        )}
      >
        {children}
      </ul>
    </div>
  );
}
